<template>
  <div>
    <form @submit.prevent="submit" v-if="!inputConfirmCode">
      <p v-if="contestRedirect" class="contest-entry-notice">{{ $t("signUpPage.contestEntryNotice") }}</p>
      <h1>{{ $t("signUpPage.title") }}</h1>
      <router-link class="sign-in-link" :to="signInLocation">{{ $t("signUpPage.signInLink") }}</router-link>
      <div class="message" v-if="message">{{ message }}</div>
      <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
      <text-field
        class="input"
        type="email"
        name="email"
        :label="$t('signUpPage.emailLabel')"
        :placeholder="$t('signUpPage.emailPlaceholder')"
        v-model="email"
      />
      <text-field
        class="input"
        type="password"
        :label="$t('signUpPage.passwordLabel')"
        :placeholder="$t('signUpPage.passwordPlaceholder')"
        v-model="password"
      />
      <text-field
        class="input"
        type="password"
        :label="$t('signUpPage.passwordConfirmLabel')"
        :placeholder="$t('signUpPage.passwordConfirmPlaceholder')"
        v-model="passwordConfirm"
      />
      <div class="attention">{{ $t("signUpPage.passwordRequirement") }}</div>
      <div class="sign-up-container">
        <p class="sign-up-txt">
          {{ $t("signUpPage.agreementText") }}
          <a href="/terms" target="_blank">{{ $t("signUpPage.termsOfService") }}</a
          >{{ $t("signUpPage.and") }}<a href="/privacy" target="_blank">{{ $t("signUpPage.privacyPolicy") }}</a>
          <span v-html="$t('signUpPage.agreementTextSuffix')"></span>
        </p>
        <checkbox-item
          v-if="currentLocale === 'ja'"
          v-model="subscribeEmailMagazine"
          class="email-magazine-checkbox"
        >
          {{ $t("signUpPage.subscribeEmailMagazine") }}
        </checkbox-item>
        <button-default class="enrollment-button" :disabled="submitting">
          <span v-if="submitting" class="enrollment-button-content">
            <icon-spinner class="enrollment-button-spinner" />
            {{ $t("signUpPage.signingUp") }}
          </span>
          <span v-else>{{ $t("signUpPage.signUpButton") }}</span>
        </button-default>
      </div>
      <div class="setting">
        <router-link :to="{ name: 'resendConfirmCode', params: { propEmail: email } }">
          {{ $t("signUpPage.resendConfirmCode") }}
        </router-link>
        <a :href="`${help}#a9ab790429a64fae9489d2ac06671565`" target="_blank">{{
          $t("signUpPage.confirmCodeNotDelivered")
        }}</a>
      </div>
    </form>
    <sign-up-confirm-code v-else :email="email" @complete="complete" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Location } from "vue-router";
import TextField from "@/components/molecules/TextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import CheckboxItem from "@/components/atoms/CheckboxItem.vue";
import SignUpConfirmCode from "@/components/organisms/SignUpConfirmCode.vue";
import { Auth } from "aws-amplify";
import { Tutorial } from "@/lib/models/tutorial";
import { createHomeIntroSlidesTutorial } from "@/lib/utils/tutorial";
import { writerTypeIndicatorRequestType } from "@/store/modules/writerTypeIndicator";
import { UserEmailClient } from "@/lib/clients/userEmailClient";
import { toReceiveAggregation } from "@/lib/utils/userEmailSettings";
import { resolveSafeRedirect, withRedirect } from "@/lib/utils/authRedirect";
import IconSpinner from "~icons/figma/Spinner";

const userEmailClient = new UserEmailClient();

interface Data {
  email: string;
  password: string;
  passwordConfirm: string;
  message: string;
  errorMessage: string;
  inputConfirmCode: boolean;
  subscribeEmailMagazine: boolean;
  submitting: boolean;
}

interface Computed {
  help: string;
  currentLocale: string;
  contestRedirect: string | null;
  signInLocation: Location;
}

interface Methods {
  complete(): Promise<void>;
  submit(): Promise<void>;
}

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: { ButtonDefault, IconSpinner, TextField, SignUpConfirmCode, CheckboxItem },
  data() {
    return {
      email: "",
      password: "",
      passwordConfirm: "",
      message: "",
      errorMessage: "",
      inputConfirmCode: false,
      subscribeEmailMagazine: false,
      submitting: false,
    };
  },
  computed: {
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
    // 現在の UI 表示言語。将来対応言語が増えた場合に判定式を変えやすくするため、
    // ロケール文字列のまま保持する。
    currentLocale() {
      return this.$i18n.locale;
    },
    contestRedirect() {
      return resolveSafeRedirect(this.$route.query.redirect);
    },
    signInLocation() {
      return withRedirect({ name: "signIn" }, this.contestRedirect);
    },
  },
  methods: {
    async complete() {
      const { email, password, $store } = this;
      await Auth.signIn(email, password);

      // 作家タイプ診断のゲストデータ確認
      const requestType: writerTypeIndicatorRequestType = "signup";
      this.$store.dispatch("writerTypeIndicatorModule/checkGuestResult", requestType);

      // サンプルデータを作成
      await $store.dispatch("generalModule/createSampleData");

      // GDPR対策: 日本語ユーザーのみメルマガ受信希望を保存する。
      // 会員登録時に明示的に選択しているため、以降ホーム画面のメルマガ確認ポップアップは出さない。
      // 失敗しても会員登録は完了させたいので、エラーは握りつぶす（あとで MyPage から変更可能）。
      if (this.currentLocale === "ja") {
        try {
          await userEmailClient.updateEmailReceiveSettings(toReceiveAggregation(this.subscribeEmailMagazine));
          $store.commit("generalModule/setShownEmailMagazineConfirmInSession", true);
        } catch (error) {
          console.error("Failed to save email magazine preference on signup", error);
        }
      }

      // チュートリアルデータを作成してトップページへ
      const tutorial: Tutorial = {
        screen: "home",
        category: "first",
        step: "before",
      };
      await Promise.all([
        $store.dispatch("tutorialModule/createTutorial", { tutorial }),
        createHomeIntroSlidesTutorial($store),
      ]);
      this.$router.push(this.contestRedirect ?? { name: "home" });
    },
    async submit() {
      if (this.submitting) return;

      const { email, password, passwordConfirm } = this;
      if (!password || !email) {
        this.errorMessage = this.$t("signUpPage.validation.emailAndPasswordRequired") as string;
        return;
      }
      if (password !== passwordConfirm) {
        this.errorMessage = this.$t("signUpPage.validation.passwordMismatch") as string;
        return;
      }
      // eslint-disable-next-line no-useless-escape
      if (!email.match(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/)) {
        this.errorMessage = this.$t("signUpPage.validation.invalidEmailFormat") as string;
        return;
      }

      this.submitting = true;
      try {
        await Auth.signUp({
          username: email,
          password,
        });
        this.message = this.$t("signUpPage.validation.emailSent") as string;
        this.errorMessage = "";
        this.inputConfirmCode = true;
      } catch (err: any) {
        if (err.code === "UsernameExistsException") {
          this.errorMessage = this.$t("signUpPage.validation.emailAlreadyExists") as string;
        } else if (err.code === "InvalidParameterException" || err.code === "InvalidPasswordException") {
          this.errorMessage = this.$t("signUpPage.validation.invalidPassword") as string;
        } else if (err.code === "LimitExceededException") {
          this.errorMessage = this.$t("signUpPage.validation.limitExceeded") as string;
        } else {
          console.log(err);
          this.errorMessage = `${this.$t("signUpPage.validation.genericError")}: ${err}`;
        }
      } finally {
        this.submitting = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
h1 {
  font-size: var(--font-size-md);
  text-align: center;
  color: var(--color-text-normal);
}

.contest-entry-notice {
  margin: 0 0 12px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  background-color: var(--color-warning);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
  color: var(--color-text-on-fixed-bg);
}
.sign-in-link {
  color: var(--color-primary);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
}

.attention {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
  margin-bottom: 15px;
}

.message {
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.error-message {
  color: var(--color-warning);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.input {
  margin-bottom: 15px;
  --text-field-bg: var(--color-surface-base);
  --text-field-border: none;
  --text-field-icon-color: var(--color-text-low);
}

.setting {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    color: var(--color-primary);
  }
}

.sign-up-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;

  .sign-up-txt {
    color: var(--color-text-normal);
    margin: 0 0 15px;
    font-size: var(--font-size-xs);

    @include mq(sp) {
      font-size: var(--font-size-xs);
    }

    a {
      text-decoration: none;
      color: var(--color-primary);
    }
  }

  .email-magazine-checkbox {
    align-self: center;
    margin: 0 0 18px;
    font-size: var(--font-size-xs);
    cursor: pointer;

    :deep(.icon) {
      margin-right: 8px;
    }
  }

  .enrollment-button {
    margin: 0;
    width: 100%;
    border-radius: var(--radius-rounded);
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  .enrollment-button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .enrollment-button-spinner {
    font-size: var(--font-size-md);
    color: currentColor;
    animation: enrollment-spinner-rotate 0.8s linear infinite;
  }
}

@keyframes enrollment-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
